package com.r_estrategica.redacaoestrategica.ui

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Bolt
import androidx.compose.material.icons.filled.MonetizationOn
import androidx.compose.material.icons.filled.WorkspacePremium
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.StrokeCap
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.r_estrategica.redacaoestrategica.data.db
import kotlinx.coroutines.await

// --- MODELO DE DADOS ---
data class UserData(
    val nome: String = "Visitante",
    val moedas: Int = 0,
    val xp: Int = 0,
    val level: Int = 1,
    val xpGoal: Int = 1000
)

@Composable
fun DashboardScreen(uid: String, nameFromGoogle: String) {
    // 1. ESTADOS DE INTERFACE
    // Usamos nameFromGoogle como chave do remember para que, se o nome mudar (ex: login concluído), o estado resete
    var userData by remember(nameFromGoogle) { mutableStateOf(UserData(nome = nameFromGoogle)) }
    var isLoading by remember(uid) { mutableStateOf(uid.isNotBlank()) }
    val scrollState = rememberScrollState()

    // 2. LÓGICA DE BUSCA NO FIRESTORE (LaunchedEffect)
    LaunchedEffect(uid) {
        if (uid.isNotBlank()) {
            try {
                // Acessa coleção 'usuarios' documento [uid]
                val doc = db.collection("usuarios").doc(uid).get().await()
                
                if (doc.exists) {
                    val data = doc.data().asDynamic()
                    
                    // Safe parsing for dynamic data from JS object
                    val fetchedName = data["nome"]?.toString()
                    val fetchedMoedas = (data["moedas"] as? Double) ?: (data["moedas"]?.toString()?.toDoubleOrNull()) ?: 0.0
                    val fetchedXp = (data["xp"] as? Double) ?: (data["xp"]?.toString()?.toDoubleOrNull()) ?: 0.0
                    val fetchedLevel = (data["level"] as? Double) ?: (data["level"]?.toString()?.toDoubleOrNull()) ?: 1.0
                    val fetchedXpGoal = (data["xpGoal"] as? Double) ?: (data["xpGoal"]?.toString()?.toDoubleOrNull()) ?: 1000.0

                    userData = UserData(
                        nome = if (!fetchedName.isNullOrBlank()) fetchedName else nameFromGoogle,
                        moedas = fetchedMoedas.toInt(),
                        xp = fetchedXp.toInt(),
                        level = fetchedLevel.toInt(),
                        xpGoal = fetchedXpGoal.toInt()
                    )
                    console.log("Firestore Data Loaded:", userData)
                }
            } catch (e: Exception) {
                console.error("Erro ao carregar dados do Firestore:", e)
            } finally {
                isLoading = false
            }
        } else {
            isLoading = false
        }
    }

    // 3. UI DA TELA
    if (isLoading) {
        Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
            CircularProgressIndicator(color = Color(0xFF1E88E5))
        }
    } else {
        Column(
            modifier = Modifier
                .fillMaxSize()
                .background(Color(0xFFF8F9FA))
                .verticalScroll(scrollState)
                .padding(24.dp)
        ) {
            // CABEÇALHO DINÂMICO
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Column {
                    Text(
                        text = "Olá, ${userData.nome}! 👋",
                        fontSize = 24.sp,
                        fontWeight = FontWeight.Bold,
                        color = Color(0xFF1E293B)
                    )
                    Text(
                        text = "Continue praticando para dominar a redação. Você está no nível ${userData.level}.",
                        fontSize = 14.sp,
                        color = Color(0xFF64748B)
                    )
                }

                // Badge de Moedas Real
                Surface(
                    color = Color(0xFFFFF9C4),
                    shape = RoundedCornerShape(12.dp),
                    modifier = Modifier.height(40.dp)
                ) {
                    Row(
                        modifier = Modifier.padding(horizontal = 12.dp),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Icon(Icons.Default.MonetizationOn, null, tint = Color(0xFFFBC02D), modifier = Modifier.size(20.dp))
                        Spacer(Modifier.width(6.dp))
                        Text("${userData.moedas}", fontWeight = FontWeight.Bold, color = Color(0xFF827717))
                    }
                }
            }

            Spacer(Modifier.height(32.dp))

            // CARD DE PROGRESSO (XP)
            Card(
                modifier = Modifier.fillMaxWidth(),
                colors = CardDefaults.cardColors(containerColor = Color.White),
                elevation = CardDefaults.cardElevation(2.dp),
                shape = RoundedCornerShape(16.dp)
            ) {
                Column(Modifier.padding(20.dp)) {
                    Row(verticalAlignment = Alignment.CenterVertically) {
                        Icon(Icons.Default.Bolt, null, tint = Color(0xFF1E88E5))
                        Spacer(Modifier.width(8.dp))
                        Text("XP Diário — Nível ${userData.level}", fontWeight = FontWeight.Bold)
                        Spacer(Modifier.weight(1f))
                        Text("${userData.xp} / ${userData.xpGoal} XP", fontSize = 12.sp, color = Color.Gray)
                    }

                    Spacer(Modifier.height(12.dp))

                    // Barra de progresso real baseada no XP
                    val progresso = userData.xp.toFloat() / userData.xpGoal.toFloat()
                    LinearProgressIndicator(
                        progress = { progresso.coerceIn(0f, 1f) },
                        modifier = Modifier.fillMaxWidth().height(10.dp),
                        color = Color(0xFF1E88E5),
                        trackColor = Color(0xFFE3F2FD),
                        strokeCap = StrokeCap.Round
                    )
                }
            }
            
            Spacer(Modifier.height(24.dp))
            
            // Botão Premium (Opcional)
            Button(
                onClick = { /* Navegar para loja */ },
                modifier = Modifier.fillMaxWidth(),
                colors = ButtonDefaults.buttonColors(containerColor = Color(0xFFFFD700)),
                shape = RoundedCornerShape(12.dp)
            ) {
                Icon(Icons.Default.WorkspacePremium, null, tint = Color(0xFF8B4513))
                Spacer(Modifier.width(8.dp))
                Text("Seja Premium", color = Color(0xFF8B4513), fontWeight = FontWeight.Bold)
            }
        }
    }
}
